import { ConfirmStorageChangeModal } from '@/components/admin/ConfirmStorageChangeModal';
import { StorageBackendForm } from '@/components/admin/StorageBackendForm';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import type {
  AdminStorageConfig,
  StorageConfigInput,
  StorageType,
  StorageUsage,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, HardDrive, XCircle } from 'lucide-react';
import { useState } from 'react';

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const idx = Math.min(units.length - 1, Math.floor(Math.log10(n) / 3));
  const v = n / 10 ** (idx * 3);
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[idx]}`;
}

function lastTypeFromConfig(c: AdminStorageConfig | undefined): StorageType {
  if (!c) return 's3';
  if (c.configured) return c.type;
  return c.lastType ?? 's3';
}

function isBackendIdentityChanged(
  current: AdminStorageConfig | undefined,
  next: StorageConfigInput,
): boolean {
  if (!current || !current.configured) return false;
  if (current.type !== next.type) return true;
  if (next.type === 's3' && current.type === 's3') {
    return current.endpoint !== next.endpoint || current.bucket !== next.bucket;
  }
  if ((next.type === 'fs' || next.type === 'nfs') && current.type !== 's3') {
    return current.rootPath !== next.rootPath;
  }
  return true;
}

export function AdminFileStorage() {
  const qc = useQueryClient();
  const cfgQuery = useQuery({
    queryKey: ['admin', 'storage'],
    queryFn: () => api.adminStorageGet(),
    refetchInterval: 30_000,
  });
  const healthQuery = useQuery({
    queryKey: ['admin', 'system-health'],
    queryFn: () => api.adminSystemHealth(),
    refetchInterval: 15_000,
  });

  const [testResult, setTestResult] = useState<null | { ok: boolean; message: string }>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ config: StorageConfigInput } | null>(null);

  const testMut = useMutation({
    mutationFn: (body: StorageConfigInput) => api.adminStorageTest(body),
    onSuccess: (res) => setTestResult({ ok: res.ok, message: res.message }),
    onError: (err: unknown) =>
      setTestResult({
        ok: false,
        message: err instanceof ApiError ? err.message : '알 수 없는 오류',
      }),
  });

  const updateMut = useMutation({
    mutationFn: (args: { config: StorageConfigInput; confirm: boolean }) =>
      api.adminStorageUpdate({
        config: args.config,
        confirmReplaceExistingFiles: args.confirm,
      }),
    onSuccess: (res, vars) => {
      if (res.ok) {
        setServerError(null);
        setPending(null);
        qc.invalidateQueries({ queryKey: ['admin', 'storage'] });
        qc.invalidateQueries({ queryKey: ['admin', 'system-info'] });
        qc.invalidateQueries({ queryKey: ['admin', 'system-health'] });
        return;
      }
      // 서버 안전망 — 클라이언트가 미리 띄우지 못한 경우 (다중 탭 race / 직접 API 호출).
      // 방금 보낸 config 로 다시 모달을 띄운다.
      setPending({ config: vars.config });
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const handleSubmit = (config: StorageConfigInput) => {
    setServerError(null);
    setTestResult(null);
    const cfg = cfgQuery.data;
    const willReplace = isBackendIdentityChanged(cfg, config) && (cfg?.existingFileCount ?? 0) > 0;
    if (willReplace) {
      setPending({ config });
      return;
    }
    updateMut.mutate({ config, confirm: false });
  };

  const handleTest = (config: StorageConfigInput) => {
    setTestResult(null);
    testMut.mutate(config);
  };

  const handleConfirmReplace = () => {
    if (!pending) return;
    updateMut.mutate({ config: pending.config, confirm: true });
  };

  const cfg = cfgQuery.data;

  return (
    <PageShell
      header={
        <PageHeader
          title="파일 저장소"
          description="첨부파일이 저장될 백엔드를 선택합니다. S3 호환 객체 스토리지 / NFS / Local 파일시스템 중 하나만 활성화됩니다."
        />
      }
    >
      {cfgQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : cfgQuery.error ? (
        <p className="text-sm text-destructive">
          {cfgQuery.error instanceof ApiError
            ? cfgQuery.error.message
            : '설정을 불러오지 못했습니다.'}
        </p>
      ) : (
        <div className="space-y-6">
          <CurrentStatusCard config={cfg} health={healthQuery.data?.storage} />

          <section className="bg-surface border border-border rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-1">백엔드 변경 / 편집</h2>
            <p className="text-sm text-foreground-muted mb-4">
              아래 탭에서 종류를 선택하고 입력 후 저장하세요. 보안상 Secret/AccessKey 는 매 저장 시
              다시 입력합니다.
            </p>
            <StorageBackendForm
              initialType={lastTypeFromConfig(cfg)}
              accessKeyMaskedHint={
                cfg?.configured && cfg.type === 's3' ? cfg.accessKeyMasked : null
              }
              initialTempCachePath={
                cfg?.configured && cfg.type === 's3' ? (cfg.tempCachePath ?? null) : null
              }
              onSubmit={handleSubmit}
              onTest={handleTest}
              busy={updateMut.isPending || testMut.isPending}
              testing={testMut.isPending}
              testResult={testResult}
              serverError={serverError}
              submitLabel={updateMut.isPending ? '저장 중…' : '저장'}
            />
          </section>
        </div>
      )}

      <ConfirmStorageChangeModal
        open={pending !== null}
        existingFileCount={cfg?.existingFileCount ?? 0}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirmReplace}
        busy={updateMut.isPending}
      />
    </PageShell>
  );
}

function CurrentStatusCard({
  config,
  health,
}: {
  config: AdminStorageConfig | undefined;
  health: { status: 'ok' | 'error' | 'skipped'; message: string | null } | undefined;
}) {
  if (!config) return null;
  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <HardDrive className="size-5 text-foreground-muted" />
          <h2 className="text-lg font-semibold">현재 상태</h2>
        </div>
        <HealthChip status={health?.status} message={health?.message} />
      </header>

      {!config.configured ? (
        <p className="text-sm text-foreground-muted">
          파일 저장소가 아직 설정되지 않았습니다. 아래에서 백엔드를 선택해 등록해 주세요.
        </p>
      ) : (
        <dl className="space-y-2.5">
          <Row k="종류" v={typeLabel(config.type)} />
          {config.type === 's3' ? (
            <>
              <Row k="엔드포인트" v={config.endpoint} mono />
              <Row k="버킷" v={config.bucket} mono />
              <Row k="리전" v={config.region} mono />
              <Row k="Access Key" v={config.accessKeyMasked} mono />
            </>
          ) : (
            <Row k="경로" v={config.rootPath} mono />
          )}
          <Row k="등록된 파일 수" v={config.existingFileCount.toLocaleString('ko-KR')} />
          <Row k="갱신" v={formatTime(config.updatedAt)} />
        </dl>
      )}

      {config.configured && config.type !== 's3' ? (
        <div className="mt-5 pt-5 border-t border-border">
          <div className="text-sm font-medium mb-2">디스크 사용량</div>
          {config.usage ? (
            <UsageBar usage={config.usage} />
          ) : (
            <p className="text-sm text-foreground-muted">
              사용량을 가져오지 못했습니다 (마운트 stale / 권한 부족 가능성).
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}

function HealthChip({
  status,
  message,
}: {
  status?: 'ok' | 'error' | 'skipped';
  message?: string | null;
}) {
  if (!status || status === 'skipped') {
    return <span className="text-xs text-foreground-muted">미설정</span>;
  }
  if (status === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[hsl(140_60%_35%)]">
        <CheckCircle2 className="size-3.5" /> 정상
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-destructive"
      title={message ?? undefined}
    >
      <XCircle className="size-3.5" /> 오류
    </span>
  );
}

function UsageBar({ usage }: { usage: StorageUsage }) {
  const total = Math.max(usage.total, 1);
  const used = Math.min(usage.used, total);
  const pct = Math.round((used / total) * 100);
  const danger = pct >= 90;
  const warn = pct >= 75 && pct < 90;
  return (
    <div className="space-y-2">
      <div className="h-2 w-full rounded-full bg-surface-muted overflow-hidden">
        <div
          className={
            danger
              ? 'h-full bg-destructive'
              : warn
                ? 'h-full bg-[hsl(40_85%_55%)]'
                : 'h-full bg-accent'
          }
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground-muted">
        <span>
          사용 <strong className="text-foreground">{formatBytes(used)}</strong> / 총{' '}
          {formatBytes(total)} ({pct}%)
        </span>
        <span>
          사용 가능 <strong className="text-foreground">{formatBytes(usage.available)}</strong>
        </span>
      </div>
    </div>
  );
}

function typeLabel(t: StorageType): string {
  if (t === 's3') return 'S3 호환 객체 스토리지';
  if (t === 'nfs') return 'NFS 마운트';
  return 'Local 파일시스템';
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR');
  } catch {
    return iso;
  }
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <dt className="w-32 text-foreground-muted">{k}</dt>
      <dd className={mono ? 'font-mono break-all' : 'break-all'}>{v}</dd>
    </div>
  );
}
